<template>
  <div class="price">
    <div class="price__label" v-if="label">{{ label }}</div>
    <div class="price__value">
      <Spinner v-if="loading" />
      <template v-else>{{ formattedValue }}</template>
    </div>
  </div>
</template>

<script setup lang="ts">
  import Spinner from '@/components/icons/Spinner.vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import { useUnits } from '@/composables/useUnits.ts'
  import { computed } from 'vue'

  const { t } = useStrings()
  const { formatPowerPrice } = useUnits()

  const props = defineProps({
    label: {
      type: String,
    },
    value: {
      type: [Number, Boolean],
      required: false,
    },
    unit: {
      type: String,
      default: 'cent',
    },
    extraClasses: {
      type: Array<string>,
      default: () => [],
    },
    noValueLabel: {
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    }
  })

  const classes = computed(() => {
    let classes = ['price']
    return classes.concat(props.extraClasses).join(' ')
  })

  const formattedValue = computed(() => {
    if (props.value === false) {
      return t(props.noValueLabel ?? 'price.noSupplyPossible')
    }
    if (isNaN(parseFloat(props.value as any))) {
      return props.value
    }
    return formatPowerPrice(props.value, 'cent')
  })
</script>

<style lang="scss" scoped>
.price {
  transition: $transition-base;
  min-width: $price-min-width-min;
  padding-right: $spacer05;

  @media (min-width: $bp-xs) {
    min-width: $price-min-width-xs;
  }

  @media (min-width: $bp-ssm) {
    min-width: $price-min-width-ssm;
  }

  &__label {
    font-size: $font-size-sm;
    color: $grey-dark;
    line-height: $line-height-sm;
  }

  &__value {
    line-height: $line-height-sm;

    @media (min-width: $bp-xs) {
      font-size: $font-size-lg;
    }

    @media (min-width: $bp-md) {
      font-size: $font-size-xl;
      margin-top: $spacer01;
    }
  }
}
</style>
